import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { getMarketingT } from '../../utils/marketingTranslations';

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'ar', label: 'AR' },
];

function MarketingLayout({ children }) {
  const location = useLocation();
  const { language, setLanguage, theme, toggleTheme } = useApp();
  const mt = getMarketingT(language);
  const isRTL = language === 'ar';
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const NAV_LINKS = [
    { path: '/', label: mt('nav_home') },
    { path: '/services', label: mt('nav_services') },
    { path: '/pricing', label: mt('nav_pricing') },
    { path: '/contact', label: mt('nav_contact') },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--bg-primary)',
      fontFamily: isRTL ? 'var(--font-arabic)' : 'var(--font-body)',
      color: 'var(--text-primary)',
      transition: 'background-color var(--transition), color var(--transition)',
    }}>
      {/* ── Navbar ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        transition: 'all 0.3s ease',
        background: scrolled ? 'var(--bg-glass)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(16px)' : 'none',
        boxShadow: scrolled ? 'var(--shadow-sm)' : 'none',
      }}>
        <div style={{
          maxWidth: 1200, margin: '0 auto', padding: '16px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
            <span style={{ fontSize: 24, color: 'var(--accent)' }}>◉</span>
            <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>Wamenu</span>
          </Link>

          {/* Desktop links */}
          <div style={{ display: 'flex', gap: 32 }} className="mk-desktop-only">
            {NAV_LINKS.map(link => (
              <Link key={link.path} to={link.path} style={{
                textDecoration: 'none', fontSize: 15, fontWeight: location.pathname === link.path ? 600 : 500,
                color: location.pathname === link.path ? 'var(--text-primary)' : 'var(--text-secondary)',
                transition: 'color 0.2s',
              }}>
                {link.label}
              </Link>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {LANGUAGES.map(l => (
              <button key={l.code} onClick={() => setLanguage(l.code)} style={{
                padding: '5px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                background: language === l.code ? 'var(--accent-light)' : 'transparent',
                color: language === l.code ? 'var(--accent)' : 'var(--text-muted)',
                border: 'none', cursor: 'pointer', transition: 'all 0.2s',
              }}>
                {l.label}
              </button>
            ))}
            <button onClick={toggleTheme} style={{
              width: 36, height: 36, borderRadius: 8, border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--bg-tertiary)', cursor: 'pointer', fontSize: 16,
              transition: 'all 0.2s',
            }}>
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <Link to="/r/casa-bella" className="mk-desktop-only" style={{
              display: 'inline-flex', padding: '8px 18px',
              background: 'var(--accent)', color: 'var(--text-inverse)',
              borderRadius: 8, textDecoration: 'none', fontSize: 13, fontWeight: 600,
            }}>
              {mt('nav_demo')}
            </Link>
            <button className="mk-mobile-only" onClick={() => setMobileOpen(!mobileOpen)} style={{
              display: 'none', flexDirection: 'column', gap: 4,
              background: 'none', border: 'none', cursor: 'pointer', padding: 8,
            }}>
              <span style={{ display: 'block', width: 20, height: 2, background: 'var(--text-primary)', borderRadius: 2, transition: 'all 0.3s', transform: mobileOpen ? 'rotate(45deg) translate(4px, 4px)' : 'none' }} />
              <span style={{ display: 'block', width: 20, height: 2, background: 'var(--text-primary)', borderRadius: 2, transition: 'all 0.3s', opacity: mobileOpen ? 0 : 1 }} />
              <span style={{ display: 'block', width: 20, height: 2, background: 'var(--text-primary)', borderRadius: 2, transition: 'all 0.3s', transform: mobileOpen ? 'rotate(-45deg) translate(4px, -4px)' : 'none' }} />
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="mk-mobile-only" style={{
            display: 'none', flexDirection: 'column', padding: '8px 24px 24px',
            background: 'var(--bg-glass)', backdropFilter: 'blur(16px)', gap: 4,
          }}>
            {NAV_LINKS.map(link => (
              <Link key={link.path} to={link.path} style={{
                textDecoration: 'none', fontSize: 16, fontWeight: location.pathname === link.path ? 600 : 500,
                color: location.pathname === link.path ? 'var(--accent)' : 'var(--text-secondary)',
                padding: '12px 0', borderBottom: '1px solid var(--border-light)',
              }}>
                {link.label}
              </Link>
            ))}
            <Link to="/r/casa-bella" style={{
              marginTop: 12, textAlign: 'center', padding: '14px 24px',
              background: 'var(--accent)', color: 'var(--text-inverse)',
              borderRadius: 10, textDecoration: 'none', fontSize: 16, fontWeight: 600,
            }}>
              {mt('nav_demo')} →
            </Link>
          </div>
        )}
      </nav>

      {/* ── Page content ── */}
      <main style={{ flex: 1 }}>{children}</main>

      {/* ── Footer ── */}
      <footer style={{
        marginTop: 'auto', background: 'var(--bg-secondary)',
        borderTop: '1px solid var(--border)', padding: '64px 24px 32px',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="mk-footer-grid" style={{
            display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr',
            gap: 48, marginBottom: 48,
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <span style={{ fontSize: 24, color: 'var(--accent)' }}>◉</span>
                <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>Wamenu</span>
              </div>
              <p style={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6, maxWidth: 280 }}>{mt('footer_tagline')}</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 8 }}>{mt('footer_product')}</h4>
              <Link to="/services" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 14 }}>{mt('nav_services')}</Link>
              <Link to="/pricing" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 14 }}>{mt('nav_pricing')}</Link>
              <Link to="/r/casa-bella" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 14 }}>{mt('nav_demo')}</Link>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 8 }}>{mt('footer_company')}</h4>
              <Link to="/contact" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 14 }}>{mt('nav_contact')}</Link>
              <a href="https://wa.me/+212722779197" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 14 }} target="_blank" rel="noreferrer">WhatsApp</a>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 8 }}>{mt('footer_legal')}</h4>
              <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 14 }}>{mt('footer_privacy')}</Link>
              <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 14 }}>{mt('footer_terms')}</Link>
            </div>
          </div>
          <div style={{
            borderTop: '1px solid var(--border)', paddingTop: 24,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
          }}>
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>© {new Date().getFullYear()} Wamenu. {mt('footer_rights')}</p>
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{mt('footer_made')}</p>
          </div>
        </div>
      </footer>

      {/* Responsive CSS — only for things inline styles can't do */}
      <style>{`
        @media (max-width: 768px) {
          .mk-desktop-only { display: none !important; }
          .mk-mobile-only { display: flex !important; }
          .mk-footer-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media (min-width: 769px) {
          .mk-mobile-only { display: none !important; }
        }
        @media (max-width: 480px) {
          .mk-footer-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

export default MarketingLayout;